import { useEffect, useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import SheetModal from '@/components/General/SheetModal';
import Callout from '@/components/General/Callout';
import Unicon from '@/components/General/Unicon';
import Chip from '@/components/General/Chip';
import Input from '@/components/General/inputs/Input';
import InputSection from '@/components/General/inputs/InputSection';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import Toggle from '@/components/General/inputs/Toggle';
import SolidButton from '@/components/General/SolidButton';
import { Mutation } from '@/utils/react-query/Mutations';
import {
    buildMealDatesPayload,
    describeMealImpact,
    formatMealDate,
    getMealSlotState,
    normalizeMealDates,
    toMealDateKey,
} from '@/utils/followUpCare';

/**
 * MealDatesSheet — enable, edit, or disable dated meal assistance on a care
 * episode (spec §13.4–13.5). One needed meal per date; the server dedupes
 * date keys so a resubmission can never double-book (C05).
 *
 * The submit is two-phase when removals touch commitments: the first call
 * comes back as a preview (applied:false + impact rows naming each staffed
 * or delivered slot that would be cancelled); the user explicitly confirms
 * and the resubmission carries confirmImpact. Commitments are never
 * silently erased (C11).
 */
const MealDatesSheet = ({ isOpen, onClose, organizationID, personID, careEpisodeID, mealSlots = [] }) => {
    const openSlots = (mealSlots || []).filter((s) => s.status === 'OPEN' && !s.cancelledAt);
    const seededDates = openSlots.map((s) => s.mealDate);
    const firstSlot = openSlots[0] || null;

    const [enabled, setEnabled] = useState(true);
    const [dates, setDates] = useState([]);
    const [deliveryInstructions, setDeliveryInstructions] = useState('');
    const [deliveryWindow, setDeliveryWindow] = useState('');
    const [draftDate, setDraftDate] = useState(null);
    const [pendingConfirm, setPendingConfirm] = useState(null); // server preview response
    const [validationError, setValidationError] = useState(null);

    // Seed from the live roster each time the sheet opens.
    useEffect(() => {
        if (isOpen) {
            setEnabled(openSlots.length > 0);
            setDates(normalizeMealDates(seededDates));
            setDeliveryInstructions(firstSlot?.deliveryInstructions || '');
            setDeliveryWindow(firstSlot?.deliveryWindow || '');
            setDraftDate(null);
            setPendingConfirm(null);
            setValidationError(null);
        }
    }, [isOpen]);

    const setMutation = Mutation.followUp.setMealDates({
        onSuccess: (data) => {
            if (data?.applied === false) {
                // Preview: the change would cancel staffed or delivered
                // commitments (or meals are being disabled). Show the
                // impact; the explicit confirm resubmits with confirmImpact.
                setPendingConfirm(data);
                return;
            }
            const added = (data?.added || []).length;
            const cancelled = (data?.cancelled || []).length;
            const parts = [];
            if (data?.enabled === false) parts.push('Meals turned off');
            if (added) parts.push(`${added} date${added === 1 ? '' : 's'} added`);
            if (cancelled) parts.push(`${cancelled} date${cancelled === 1 ? '' : 's'} cancelled`);
            Toast.show({ type: 'success', text2: parts.join(' · ') || 'Meal dates saved' });
            handleClose();
        },
        onError: (error) => {
            if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to save meal dates. Please try again.' });
            }
        },
    });

    const handleClose = () => {
        setPendingConfirm(null);
        setValidationError(null);
        onClose?.();
    };

    const addDate = () => {
        const key = toMealDateKey(draftDate);
        if (!key) return;
        setDates((prev) => normalizeMealDates([...prev, key]));
        setDraftDate(null);
        setValidationError(null);
    };

    const removeDate = (key) => {
        setDates((prev) => prev.filter((d) => d !== key));
        setValidationError(null);
    };

    const submit = (confirmImpact = false) => {
        if (setMutation.isPending) return;
        const { errors, payload } = buildMealDatesPayload({
            careEpisodeID,
            enabled,
            dates,
            deliveryInstructions,
            deliveryWindow,
            confirmImpact,
        });
        if (!payload) {
            setValidationError(errors[0]);
            return;
        }
        setValidationError(null);
        setMutation.mutate({ organizationID, careEpisodeID, personID, payload });
    };

    // Slots losing their date in this edit (for inline hints on the form).
    const removedDates = enabled
        ? normalizeMealDates(seededDates).filter((d) => !dates.includes(d))
        : normalizeMealDates(seededDates);
    const impactedRows = describeMealImpact(pendingConfirm?.impact || []);

    const deliveredByDate = {};
    openSlots.forEach((s) => {
        if (getMealSlotState(s) === 'DELIVERED') deliveredByDate[s.mealDate] = true;
    });

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={handleClose}
            onCancel={pendingConfirm ? () => setPendingConfirm(null) : handleClose}
            title="Meal Dates"
            subtitle="One needed meal per date"
            stayOpenOnBlur
            snapPoints={[480, 680]}
            cancelText={pendingConfirm ? 'Back' : 'Cancel'}
            confirmText={pendingConfirm ? 'Confirm Changes' : enabled ? 'Save Meal Dates' : 'Turn Off Meals'}
            onConfirm={!setMutation.isPending
                ? () => (pendingConfirm ? submit(true) : submit(false))
                : null}
            loading={setMutation.isPending}
        >
            <ScrollView style={styles.scroll} keyboardShouldPersistTaps="handled">
                {pendingConfirm ? (
                    <View testID="meal-dates-impact">
                        <Callout
                            type="warning"
                            title={pendingConfirm?.enabled === false ? 'Turn off meals?' : 'Review changes'}
                            message="These commitments are affected. Delivered meals keep their delivery record; assigned volunteers are released."
                        />
                        <View style={styles.impactList}>
                            {impactedRows.map((item) => (
                                <View key={item.mealSlotID || item.mealDate} style={styles.impactRow}>
                                    <Unicon
                                        name={item.delivered ? 'checkmark-circle-outline' : 'alert-circle-outline'}
                                        size={16}
                                        colorVariant={item.delivered ? 'success' : 'warning'}
                                    />
                                    <View style={styles.impactBody}>
                                        <Text style={styles.impactDate}>{formatMealDate(item.mealDate)}</Text>
                                        <Text style={styles.impactDesc}>{item.description}</Text>
                                    </View>
                                </View>
                            ))}
                        </View>
                    </View>
                ) : (
                    <>
                        <View style={styles.toggleRow}>
                            <View style={styles.toggleBody}>
                                <Text style={styles.toggleTitle}>Meal coordination</Text>
                                <Text style={styles.toggleHint}>
                                    {enabled
                                        ? 'Each date creates one meal slot volunteers can claim.'
                                        : 'Turning this off cancels outstanding meal slots.'}
                                </Text>
                            </View>
                            <Toggle
                                testID="meal-dates-enabled"
                                value={enabled}
                                onValueChange={(v) => { setEnabled(v); setValidationError(null); }}
                            />
                        </View>

                        {enabled && (
                            <>
                                <Text style={styles.label}>Meal dates <Text style={styles.required}>*</Text></Text>
                                {dates.map((key) => (
                                    <View key={key} style={styles.dateRow} testID={`meal-date-${key}`}>
                                        <Unicon name="calendar-outline" size={15} colorVariant="paths" />
                                        <Text style={styles.dateText}>{formatMealDate(key)}</Text>
                                        {deliveredByDate[key] && (
                                            <Chip icon="checkmark-circle-outline" colorVariant="success">Delivered</Chip>
                                        )}
                                        <Pressable
                                            testID={`meal-date-remove-${key}`}
                                            accessibilityRole="button"
                                            accessibilityLabel={`Remove ${formatMealDate(key)}`}
                                            hitSlop={8}
                                            onPress={() => removeDate(key)}
                                        >
                                            <Unicon name="close-circle-outline" size={18} colorVariant="textSecondary" />
                                        </Pressable>
                                    </View>
                                ))}
                                <View style={styles.addRow}>
                                    <View style={styles.addPicker}>
                                        <DatePickerInput
                                            title="Add a date"
                                            mode="date"
                                            value={draftDate}
                                            onUpdate={setDraftDate}
                                        />
                                    </View>
                                    <SolidButton
                                        testID="meal-dates-add-date"
                                        size="sm"
                                        variant="outlined"
                                        type="secondary"
                                        iconName="add"
                                        text="Add"
                                        disabled={!draftDate}
                                        onPress={addDate}
                                    />
                                </View>
                                {removedDates.length > 0 && (
                                    <Callout
                                        type="info"
                                        message={`Removing ${removedDates.map(formatMealDate).join(', ')} cancels that slot${removedDates.length === 1 ? '' : 's'}. Staffed and delivered dates will ask for confirmation.`}
                                    />
                                )}
                                {validationError && (
                                    <Callout type="error" message={validationError} testID="meal-dates-error" />
                                )}
                                <InputSection>
                                    <Input
                                        title="Delivery instructions (optional)"
                                        placeholder="e.g. Leave at the side door, no nuts"
                                        value={deliveryInstructions}
                                        multiline
                                        onUpdate={setDeliveryInstructions}
                                        testID="meal-dates-instructions"
                                    />
                                    <Input
                                        title="Delivery window (optional)"
                                        placeholder="e.g. Between 5 and 6 pm"
                                        value={deliveryWindow}
                                        maxLength={120}
                                        onUpdate={setDeliveryWindow}
                                        testID="meal-dates-window"
                                    />
                                </InputSection>
                            </>
                        )}

                        {!enabled && (
                            <Callout
                                type="info"
                                message="Meals will be turned off. Any staffed or delivered dates are shown for confirmation before they change."
                            />
                        )}
                    </>
                )}
            </ScrollView>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    scroll: { flexGrow: 0 },
    toggleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 12,
        marginBottom: 12,
        boxShadow: shadows.card,
    },
    toggleBody: { flex: 1, minWidth: 0 },
    toggleTitle: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    toggleHint: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
    label: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        marginTop: 4,
        marginBottom: 8,
    },
    required: { color: colors.error },
    dateRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        backgroundColor: colors.surface,
        borderRadius: 10,
        paddingHorizontal: 12,
        paddingVertical: 10,
        marginBottom: 6,
        boxShadow: shadows.card,
    },
    dateText: { flex: 1, fontSize: 14, fontFamily: fonts.medium, color: colors.text },
    addRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, marginBottom: 8 },
    addPicker: { flex: 1 },
    impactList: { marginTop: 12, gap: 8 },
    impactRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        backgroundColor: colors.surface,
        borderRadius: 10,
        padding: 12,
        boxShadow: shadows.card,
    },
    impactBody: { flex: 1, minWidth: 0 },
    impactDate: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    impactDesc: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
}));

export default MealDatesSheet;
